import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import DateSelect from "@/components/DateSelect";
import useModal from "@/hooks/useModal";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import pluralize from "@/lib/pluralize";
import { DateTime } from "luxon";
import { OrderType } from "@/pages/billing/orders/[orderId]/types";

export type ChangeDueDateModalProps = {
  onDismiss: () => void;
  orders: OrderType[];
  onSubmit: (variables: any, close: () => void, sendNotification?: boolean) => void;
  loading: boolean;
  errorContent: string;
};

export const ChangeDueDateModal = ({ onDismiss, orders, onSubmit, loading, errorContent }: ChangeDueDateModalProps) => {
  const { control, watch } = useForm();
  const orderIds = Object.keys(orders);
  const headline = `Change Due ${pluralize(orders.length, "Date")}`;
  const { close, Modal } = useModal();

  const [sendNotification, setSendNotification] = useState(false);

  const dueDate = DateTime.now().plus({ days: 30 }).toISO();

  const orderDueDateRange = watch("orderDueDate", {
    startDate: dueDate,
    endDate: dueDate
  });

  const variables = {
    orderIds: orderIds,
    dueDate: orderDueDateRange.startDate,
    sendNotification: sendNotification
  };

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button
          onClick={() => onSubmit(variables, close, sendNotification)}
          disabled={loading || !orderDueDateRange.startDate}
        >
          {headline}
        </Button>
      }
      headline={headline}
      className="w-[423px] mt-[200px]"
      displayOverflow
    >
      {errorContent ? <div className="text-red-500 font-bold text-center mb-4">{errorContent}</div> : null}
      <p>
        Changing the due {pluralize(orders.length, "date")} of the selected {pluralize(orders.length, "order")} may
        affect eligibility for suspension or termination.
      </p>
      <p className="mt-5 text-label-large">Orders to Update: {orderIds?.length}</p>
      <div className="mt-6">
        <input className="mt-4 ml-1 absolute"></input>

        <Controller
          name="orderDueDate"
          control={control}
          render={({ field }) => (
            <DateSelect
              label="Order Due Date *"
              useModal={true}
              value={orderDueDateRange}
              onChange={(e) => {
                field.onChange(e);
              }}
              useSingleDate
              showFooter={false}
            />
          )}
        />

        <div className="mt-4">
          <Checkbox
            name="send-notification"
            label="Send email notification to payor"
            checked={sendNotification}
            onChange={() => {
              setSendNotification(!sendNotification);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
